(ns shared.core.components.recharts.scatter-chart
  (:require [shared.core.components.recharts.tooltip :as tooltip]
            [shared.core.components.recharts.util :as recharts.util]
            [shared.core.style :as style]
            [shared.core.util :as util]))

(defn scatter-chart
  "A scatter (or bubble) chart supporting one or more named series.

   Required:
   - :series   — vector of series maps, each with:
                 :name  — string label for the series
                 :data  — vector of data maps
                 :color — (optional) hex/rgba fill; auto-generated if omitted

   Optional layout:
   - :x-key            — key for the x-axis value in each data map (default \"x\")
   - :y-key            — key for the y-axis value in each data map (default \"y\")
   - :z-key            — key for bubble size; when provided a ZAxis is rendered
   - :z-range          — [min max] pixel size for ZAxis bubbles (default [4 40])
   - :x-domain         — [min max] for the x-axis (default [\"auto\" \"auto\"])
   - :y-domain         — [min max] for the y-axis (default [\"auto\" \"auto\"])
   - :line?            — connect dots with a line (default false)
   - :x-axis-visible?  — (default true)
   - :y-axis-visible?  — (default true)
   - :x-grid-visible?  — (default true)
   - :y-grid-visible?  — (default true)
   - :tooltip-visible? — (default true)
   - :show-legend?     — (default false)
   - :legend-label-fn  — (fn [series-name]) for custom label display
   - :legend-config    — extra props merged into standalone-legend
   - :width            — (default \"100%\")
   - :height           — (default 300)"
  [{:keys [data
           name-key
           x-key
           y-key
           z-key
           z-range
           x-domain
           y-domain
           line?
           x-axis-config
           y-axis-config
           z-axis-config
           x-axis-visible?
           y-axis-visible?
           x-grid-visible?
           y-grid-visible?
           tooltip-visible?
           show-legend?
           legend-label-fn
           legend-config
           dynamic-margins?
           goal
           goal-config
           width
           height]
    :or {name-key :name
         x-key :x
         y-key :y
         z-range [4 40]
         x-domain ["auto" "auto"]
         y-domain ["auto" "auto"]
         line? false
         x-axis-visible? true
         y-axis-visible? true
         x-grid-visible? true
         y-grid-visible? true
         tooltip-visible? true
         show-legend? false
         dynamic-margins? true
         width "100%"
         height 300}}]
  (let [y-max-value (or (recharts.util/axis-max y-axis-config)
                        (->> data
                             (map #(or (get % y-key) (get % (keyword y-key))))
                             (filter number?)
                             (reduce max (or goal 0))))
        goal-label (when goal
                     (recharts.util/goal-label goal goal-config (:percent? y-axis-config)))
        y-axis-width (when (and dynamic-margins? y-axis-visible? (not (:width y-axis-config)))
                       (recharts.util/fit-y-axis-width [(recharts.util/tick-label y-axis-config y-max-value)]))
        x-axis-height (when (and dynamic-margins? x-axis-visible? (not (:height x-axis-config)))
                        (recharts.util/fit-x-axis-height
                          (map #(util/readable-number (or (get % x-key) (get % (keyword x-key)))) data)
                          (:angle x-axis-config)))
        grouped (group-by #(get % name-key) data)
        series-color (fn [{:keys [color] :as s}]
                       (or color
                           (some-> (get s name-key) (str) (not-empty) (recharts.util/color-from-string))
                           (style/alc-color "chart/blue")))
        legend-entries (when show-legend?
                         (->> grouped
                              (keep (fn [[scatter-name scatter-data]]
                                      (when (some? scatter-name)
                                        {:label (if legend-label-fn (legend-label-fn scatter-name) scatter-name)
                                         :color (series-color (first scatter-data))})))
                              (vec)))
        scatter-series (->> grouped
                            (mapv (fn [[scatter-name scatter-data]]
                                    [recharts.util/re-scatter
                                     (cond-> {:name scatter-name
                                              :data scatter-data
                                              :fill (series-color (first scatter-data))
                                              :line line?}
                                             line?
                                             (assoc :lineJointType "monotone"))])))]
    [:div {:style {:width width :display :flex :flex-direction :column}}
     [recharts.util/re-responsive-container {:width "100%" :height height}
      (into [recharts.util/re-scatter-chart {:margin {:top 10
                                                      :right (cond-> 20
                                                                     goal (max (recharts.util/goal-right-margin goal-label)))
                                                      :bottom 10
                                                      :left 10}}
             (when x-grid-visible?
               [recharts.util/re-cartesian-grid {:stroke (style/alc-color "lines/chart_grid")
                                                 :vertical x-grid-visible?
                                                 :horizontal y-grid-visible?}])
             [recharts.util/re-x-axis (cond-> {:dataKey x-key
                                               :type "number"
                                               :tickLine false
                                               :height (if x-axis-visible? (or x-axis-height 30) 0)
                                               :tick #(recharts.util/custom-rotated-x-tick % x-axis-config)}
                                              x-domain
                                              (assoc :domain x-domain)
                                              x-axis-config
                                              (merge (recharts.util/axis-props x-axis-config)))]
             [recharts.util/re-y-axis (cond-> {:dataKey y-key
                                               :type "number"
                                               :tickLine false
                                               :axisLine false
                                               :width (if y-axis-visible? (or y-axis-width 40) 0)
                                               :tick #(recharts.util/custom-y-tick % (select-keys y-axis-config [:percent?]))}
                                              y-domain
                                              (assoc :domain y-domain)
                                              y-axis-config
                                              (merge (recharts.util/axis-props y-axis-config)))]
             (when z-key
               [recharts.util/re-z-axis (merge {:dataKey z-key
                                                :range z-range}
                                               z-axis-config)])
             (when tooltip-visible?
               [recharts.util/re-tooltip
                {:cursor {:strokeDasharray "3 3"}
                 :content (tooltip/chart-tooltip-content
                            {:header-fn (fn [{:keys [payload]}]
                                          (:name (:payload (first payload))))})}])
             (when goal
               (recharts.util/goal-line {:goal goal
                                         :goal-config goal-config
                                         :label goal-label}))]
            scatter-series)]
     (when (not-empty legend-entries)
       [recharts.util/standalone-legend (assoc legend-config :entries legend-entries)])]))